import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface PlaceSuggestion {
  id: string;
  created_at: string;
  created_by: string | null;
  producer_id: string;
  status: 'pending' | 'approved' | 'rejected';
  suggested_name: string;
  address_line1: string | null;
  postal_code: string | null;
  city: string | null;
  region: string | null;
  country: string;
  lat: number | null;
  lng: number | null;
  note: string | null;
  admin_note: string | null;
  approved_place_id: string | null;
  handled_by: string | null;
  handled_at: string | null;
}

export type PlaceSuggestionInsert = {
  producer_id: string;
  suggested_name: string;
  address_line1?: string | null;
  postal_code?: string | null;
  city?: string | null;
  region?: string | null;
  lat?: number | null;
  lng?: number | null;
  note?: string | null;
};

/**
 * Hook for fetching all place suggestions (admin only)
 */
export function useAllPlaceSuggestions(statusFilter?: 'pending' | 'approved' | 'rejected') {
  return useQuery({
    queryKey: ["place-suggestions", "all", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("place_suggestions")
        .select(`
          *,
          producers:producer_id (id, name, slug)
        `)
        .order("created_at", { ascending: false });

      if (statusFilter) {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as (PlaceSuggestion & { producers: { id: string; name: string; slug: string } | null })[];
    },
  });
}

/**
 * Hook for fetching place suggestions for a specific producer
 */
export function useProducerPlaceSuggestions(producerId: string | undefined) {
  return useQuery({
    queryKey: ["place-suggestions", "producer", producerId],
    queryFn: async () => {
      if (!producerId) return [];
      
      const { data, error } = await supabase
        .from("place_suggestions")
        .select("*")
        .eq("producer_id", producerId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as PlaceSuggestion[];
    },
    enabled: !!producerId,
  });
}

/**
 * Hook for creating a place suggestion
 */
export function useCreatePlaceSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (suggestion: PlaceSuggestionInsert) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from("place_suggestions")
        .insert({
          ...suggestion,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as PlaceSuggestion;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["place-suggestions"] });
      toast({ 
        title: "Platsförslag skickat", 
        description: "Tack! Admin kommer att granska ditt förslag." 
      });
    },
    onError: (error) => {
      console.error("Error creating suggestion:", error);
      toast({ 
        title: "Kunde inte skicka förslag", 
        description: error.message,
        variant: "destructive"
      });
    },
  });
}

/**
 * Hook for approving a place suggestion and creating a place (admin only)
 */
export function useApprovePlaceSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const { data, error } = await supabase
        .rpc("admin_create_place_from_suggestion", { p_suggestion_id: suggestionId });

      if (error) throw error;
      return data as { success: boolean; place_id: string; slug: string; name: string };
    },
     onSuccess: (data) => {
       // Invalidate all related queries
       queryClient.invalidateQueries({ queryKey: ["place-suggestions"] });
       queryClient.invalidateQueries({ queryKey: ["admin-places"] });
       queryClient.invalidateQueries({ queryKey: ["published-places"] });
       // Also invalidate producer queries so the linked place shows immediately
       queryClient.invalidateQueries({ queryKey: ["my-producer"] });
       queryClient.invalidateQueries({ queryKey: ["producer"] });
       queryClient.invalidateQueries({ queryKey: ["my-farms"] });
       toast({
         title: "Plats skapad",
         description: `Platsen "${data.name}" har skapats och kopplats till värden.`,
       });
     },
    onError: (error) => {
      console.error("Error approving suggestion:", error);
      toast({ 
        title: "Kunde inte godkänna förslag", 
        description: error.message,
        variant: "destructive"
      });
    },
  });
}

/**
 * Hook for rejecting a place suggestion (admin only)
 */
export function useRejectPlaceSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ suggestionId, adminNote }: { suggestionId: string; adminNote?: string }) => {
      const { data, error } = await supabase
        .rpc("reject_place_suggestion", { 
          p_suggestion_id: suggestionId,
          p_admin_note: adminNote || null
        });

      if (error) throw error;
      return data as { id: string; status: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["place-suggestions"] });
      toast({ 
        title: "Förslag avvisat", 
        description: "Platsförslaget har avvisats." 
      });
    },
    onError: (error) => {
      console.error("Error rejecting suggestion:", error);
      toast({ 
        title: "Kunde inte avvisa förslag", 
        description: error.message,
        variant: "destructive"
      });
    },
  });
}
